"use client";
import React, { useEffect, useRef, useState, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useSidebar } from "../context/SidebarContext";
import {
  BoxCubeIcon,
  CalenderIcon,
  ChevronDownIcon,
  GridIcon,
  HorizontaLDots,
  ListIcon,
  PageIcon,
  PieChartIcon,
  PlugInIcon,
  TableIcon,
  UserCircleIcon,
} from "../icons/index";
import SidebarWidget from "./SidebarWidget";

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
};

const navItems: NavItem[] = [
  {
    icon: <GridIcon />,
    name: "Dashboard",
    path: "/",
  },
  {
    name: "Catalog",
    icon: <BoxCubeIcon />,
    subItems: [
      { name: "All Products", path: "/products", pro: false },
      { name: "Add Product", path: "/products/new", pro: false },
      { name: "Categories", path: "/categories", pro: false },
      { name: "Collections", path: "/catalog/collections", pro: false },
      { name: "Personalization", path: "/catalog/personalization", pro: false },
      { name: "Gift Cards", path: "/catalog/gift-cards", pro: false },
      { name: "Bundles & Hampers", path: "/catalog/bundles", pro: false },
      { name: "Occasions & Festivals", path: "/catalog/occasions", pro: false },
      { name: "Attributes & Specs", path: "/catalog/attributes", pro: false },
      { name: "Brands & Artisans", path: "/catalog/brands", pro: false },
      { name: "Customer Reviews", path: "/catalog/reviews", pro: false },
    ],
  },
  {
    name: "Inventory",
    icon: <TableIcon />,
    subItems: [
      { name: "Stock Levels", path: "/inventory", pro: false },
      { name: "Warehouses", path: "/inventory/warehouses", pro: false },
      { name: "Low Stock Alerts", path: "/inventory/low-stock", pro: false },
      { name: "MMS Inventory Sync", path: "/inventory/channel-sync", pro: false },
    ],
  },
  {
    name: "Purchasing",
    icon: <ListIcon />,
    subItems: [
      { name: "Purchase Orders", path: "/purchases", pro: false },
      { name: "Suppliers", path: "/purchasing/suppliers", pro: false },
      { name: "Goods Received (GRN)", path: "/purchasing/grn", pro: false },
    ],
  },
  {
    name: "Orders",
    icon: <PageIcon />,
    subItems: [
      { name: "All Orders", path: "/orders", pro: false },
      { name: "Customer Returns", path: "/orders/returns", pro: false },
      { name: "Courier RTO", path: "/orders/rto", pro: false },
    ],
  },
  {
    name: "Payments",
    icon: <ListIcon />,
    subItems: [
      { name: "Gateways", path: "/settings/payments", pro: false },
      { name: "Transactions", path: "/payments/transactions", pro: false },
      { name: "Refunds", path: "/payments/refunds", pro: false },
      { name: "Reconciliation", path: "/payments/reconciliation", pro: false },
    ],
  },
  {
    name: "Shipping",
    icon: <TableIcon />,
    subItems: [
      { name: "Active Shipments", path: "/shipping/shipments", pro: false },
      { name: "Serviceable Pincodes", path: "/shipping/pincodes", pro: false },
      { name: "Couriers", path: "/shipping/couriers", pro: false },
      { name: "Shipping Rules", path: "/shipping/rules", pro: false },
    ],
  },
  {
    name: "Marketing",
    icon: <PieChartIcon />,
    subItems: [
      { name: "Coupons", path: "/marketing/coupons", pro: false },
      { name: "Flash Offers", path: "/marketing/offers", pro: false },
      { name: "Campaigns", path: "/marketing/campaigns", pro: false },
      { name: "Segments", path: "/marketing/segments", pro: false },
    ],
  },
  {
    name: "CMS",
    icon: <PlugInIcon />,
    subItems: [
      { name: "Homepage Layout", path: "/cms/homepage", pro: false },
      { name: "Banners & Sliders", path: "/cms/banners", pro: false },
      { name: "Custom Pages", path: "/cms/pages", pro: false },
      { name: "Blogs & Stories", path: "/cms/blogs", pro: false },
      { name: "Landing Pages", path: "/cms/landing", pro: false },
      { name: "Support FAQs", path: "/support", pro: false },
    ],
  },
  {
    name: "Analytics",
    icon: <PieChartIcon />,
    subItems: [
      { name: "Traffic Sources", path: "/analytics/traffic", pro: false },
      { name: "Sales Performance", path: "/analytics/sales", pro: false },
      { name: "Conversion Funnel", path: "/analytics/funnel", pro: false },
    ],
  },
  {
    name: "Finance",
    icon: <ListIcon />,
    subItems: [
      { name: "GST Reports", path: "/finance/gst", pro: false },
      { name: "Invoices", path: "/finance/invoices", pro: false },
      { name: "Revenue Realization", path: "/finance/revenue", pro: false },
      { name: "Profit & Loss (P&L)", path: "/finance/profit", pro: false },
    ],
  },
  {
    name: "Settings",
    icon: <UserCircleIcon />,
    subItems: [
      { name: "General Settings", path: "/settings", pro: false },
      { name: "Payment Gateways", path: "/settings/payments", pro: false },
      { name: "Shipping Settings", path: "/settings/shipping", pro: false },
      { name: "Notifications", path: "/settings/notifications", pro: false },
      { name: "SEO & Schema", path: "/settings/seo", pro: false },
      { name: "Users & Staff", path: "/users", pro: false },
      { name: "Roles & RBAC", path: "/settings/roles", pro: false },
      { name: "Feature Flags", path: "/settings/flags", pro: false },
    ],
  },
];

const othersItems: NavItem[] = [
  {
    name: "UI Components",
    icon: <BoxCubeIcon />,
    subItems: [
      { name: "Alerts", path: "/alerts", pro: false },
      { name: "Modals", path: "/modals", pro: false },
      { name: "Buttons", path: "/buttons", pro: false },
    ],
  },
];

const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const pathname = usePathname();

  const renderMenuItems = (
    navItems: NavItem[],
    menuType: "main" | "others"
  ) => (
    <ul className="flex flex-col gap-3">
      {navItems.map((nav, index) => (
        <li key={nav.name}>
          {nav.subItems ? (
            <button
              onClick={() => handleSubmenuToggle(index, menuType)}
              className={`menu-item group ${
                openSubmenu?.type === menuType && openSubmenu?.index === index
                  ? "menu-item-active"
                  : "menu-item-inactive"
              } cursor-pointer ${
                !isExpanded && !isHovered
                  ? "lg:justify-center"
                  : "lg:justify-start"
              }`}
            >
              <span
                className={` ${
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? "menu-item-icon-active"
                    : "menu-item-icon-inactive"
                }`}
              >
                {nav.icon}
              </span>
              {(isExpanded || isHovered || isMobileOpen) && (
                <span className={`menu-item-text`}>{nav.name}</span>
              )}
              {(isExpanded || isHovered || isMobileOpen) && (
                <ChevronDownIcon
                  className={`ml-auto w-4 h-4 transition-transform duration-200 ${
                    openSubmenu?.type === menuType &&
                    openSubmenu?.index === index
                      ? "rotate-180 text-brand-500"
                      : ""
                  }`}
                />
              )}
            </button>
          ) : (
            nav.path && (
              <Link
                href={nav.path}
                className={`menu-item group ${
                  isActive(nav.path) ? "menu-item-active" : "menu-item-inactive"
                }`}
              >
                <span
                  className={`${
                    isActive(nav.path)
                      ? "menu-item-icon-active"
                      : "menu-item-icon-inactive"
                  }`}
                >
                  {nav.icon}
                </span>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <span className={`menu-item-text`}>{nav.name}</span>
                )}
              </Link>
            )
          )}
          {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
            <div
              ref={(el) => {
                subMenuRefs.current[`${menuType}-${index}`] = el;
              }}
              className="overflow-hidden transition-all duration-300"
              style={{
                height:
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? `${subMenuHeight[`${menuType}-${index}`]}px`
                    : "0px",
              }}
            >
              <ul className="mt-1 space-y-1 ml-7">
                {nav.subItems.map((subItem) => (
                  <li key={subItem.name}>
                    <Link
                      href={subItem.path}
                      className={`menu-dropdown-item text-xs py-1.5 ${
                        isActive(subItem.path)
                          ? "menu-dropdown-item-active font-bold text-primary"
                          : "menu-dropdown-item-inactive text-gray-500 hover:text-black dark:hover:text-white"
                      }`}
                    >
                      {subItem.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </li>
      ))}
    </ul>
  );

  const [openSubmenu, setOpenSubmenu] = useState<{
    type: "main" | "others";
    index: number;
  } | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>(
    {}
  );
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const isActive = useCallback((path: string) => path === pathname, [pathname]);

  useEffect(() => {
    let submenuMatched = false;
    ["main", "others"].forEach((menuType) => {
      const items = menuType === "main" ? navItems : othersItems;
      items.forEach((nav, index) => {
        if (nav.subItems) {
          nav.subItems.forEach((subItem) => {
            if (isActive(subItem.path)) {
              setOpenSubmenu({
                type: menuType as "main" | "others",
                index,
              });
              submenuMatched = true;
            }
          });
        }
      });
    });

    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [pathname, isActive]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.type}-${openSubmenu.index}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  const handleSubmenuToggle = (index: number, menuType: "main" | "others") => {
    setOpenSubmenu((prevOpenSubmenu) => {
      if (
        prevOpenSubmenu &&
        prevOpenSubmenu.type === menuType &&
        prevOpenSubmenu.index === index
      ) {
        return null;
      }
      return { type: menuType, index };
    });
  };

  return (
    <aside
      className={`fixed mt-16 flex flex-col lg:mt-0 top-0 px-4 left-0 bg-white dark:bg-gray-900 dark:border-gray-800 text-gray-900 h-screen transition-all duration-300 ease-in-out z-50 border-r border-gray-200 
        ${
          isExpanded || isMobileOpen
            ? "w-[290px]"
            : isHovered
            ? "w-[290px]"
            : "w-[90px]"
        }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`py-6 flex ${
          !isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
        }`}
      >
        <Link href="/" className="flex items-center gap-2">
          {isExpanded || isHovered || isMobileOpen ? (
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-tr from-amber-600 via-amber-500 to-yellow-400 flex items-center justify-center text-white shadow-md shadow-amber-500/20 font-bold text-sm tracking-wider">
                AL
              </div>
              <div>
                <div className="font-bold text-sm text-gray-900 dark:text-white tracking-tight flex items-center gap-1.5">
                  Artisan Living
                  <span className="text-[9px] uppercase font-extrabold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                    Admin
                  </span>
                </div>
                <div className="text-[11px] text-gray-400 font-medium">Heirloom Commerce Hub</div>
              </div>
            </div>
          ) : (
            <div className="h-9 w-9 rounded-xl bg-gradient-to-tr from-amber-600 via-amber-500 to-yellow-400 flex items-center justify-center text-white shadow-md shadow-amber-500/20 font-bold text-sm tracking-wider">
              AL
            </div>
          )}
        </Link>
      </div>
      <div className="flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar pb-10">
        <nav className="mb-6">
          <div className="flex flex-col gap-4">
            <div>
              <h2
                className={`mb-3 text-[11px] font-bold uppercase tracking-wider text-gray-400 ${
                  !isExpanded && !isHovered
                    ? "lg:justify-center"
                    : "justify-start"
                }`}
              >
                {isExpanded || isHovered || isMobileOpen ? (
                  "Management Departments"
                ) : (
                  <HorizontaLDots />
                )}
              </h2>
              {renderMenuItems(navItems, "main")}
            </div>
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default AppSidebar;
